import { captureException } from '@sentry/react';
import { create } from 'zustand';

import { InboxItem, inboxService } from '@/services/database';
import { DeltaPacket, publishLocalDeltaPacket } from '@/stores/realtime/delta';

type InboxDeltaPayload = InboxItem | { activityId?: string; readAt?: string; wasUnread?: boolean };

export type InboxNotificationDeltaPacket = DeltaPacket<
    'inbox',
    InboxDeltaPayload,
    'create' | 'update' | 'delete' | 'read' | 'read-all'
>;

type InboxNotificationState = {
    error?: string;
    hydrated: boolean;
    items: InboxItem[];
    latestToastItem?: InboxItem;
    loading: boolean;
    unreadCount: number;
    deletedItemIds: string[];
    deletingItemIds: string[];
};

type InboxNotificationActions = {
    applyInboxDelta: (packet: InboxNotificationDeltaPacket) => void;
    fetchPreview: (force?: boolean) => Promise<void>;
    markAllRead: () => Promise<void>;
    markItemRead: (activityId: string) => Promise<void>;
    deleteItem: (item: InboxItem) => Promise<void>;
};

const PREVIEW_LIMIT = 10;

function sortInboxItems(items: InboxItem[]) {
    return [...items].sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
}

function upsertInboxItem(items: InboxItem[], item: InboxItem) {
    const next = items.filter((existing) => existing.id !== item.id);

    if (item.status === 'unread') {
        next.push(item);
    }

    return sortInboxItems(next).slice(0, PREVIEW_LIMIT);
}

function mergeInboxItems(items: InboxItem[], incomingItems: InboxItem[]) {
    return incomingItems.reduce((mergedItems, item) => upsertInboxItem(mergedItems, item), items);
}

export const useInboxNotificationStore = create<InboxNotificationState & InboxNotificationActions>((set, get) => ({
    hydrated: false,
    items: [],
    loading: false,
    unreadCount: 0,
    deletedItemIds: [],
    deletingItemIds: [],
    applyInboxDelta: (packet) => {
        if (packet.entity !== 'inbox') {
            return;
        }

        const currentState = get();
        let items = currentState.items;
        let nextUnreadCount = currentState.unreadCount;
        const existingItem = items.find((item) => item.id === packet.entityId);
        const alreadyDeleted = currentState.deletedItemIds.includes(packet.entityId);

        if (packet.operation === 'delete') {
            if (alreadyDeleted) {
                return;
            }
            items = items.filter((item) => item.id !== packet.entityId);

            const wasUnread = packet.payload && 'wasUnread' in packet.payload ? packet.payload.wasUnread : false;
            if (existingItem?.status === 'unread' || (!existingItem && wasUnread)) {
                nextUnreadCount = Math.max(0, nextUnreadCount - 1);
            }
        }

        if ((packet.operation === 'create' || packet.operation === 'update') && packet.payload) {
            if (alreadyDeleted) {
                return;
            }
            const incomingItem = packet.payload as InboxItem;

            items = upsertInboxItem(items, incomingItem);

            if (!existingItem && incomingItem.status === 'unread') {
                nextUnreadCount += 1;
            } else if (existingItem?.status === 'unread' && incomingItem.status === 'read') {
                nextUnreadCount = Math.max(0, nextUnreadCount - 1);
            } else if (existingItem?.status === 'read' && incomingItem.status === 'unread') {
                nextUnreadCount += 1;
            }
        }

        if (packet.operation === 'read') {
            const activityId =
                (packet.payload as { activityId?: string } | undefined)?.activityId ??
                packet.entityId.replace(/^resource-activity:/, '');

            items = items.filter((item) => item.activity.id !== activityId && item.id !== packet.entityId);

            if (existingItem?.status === 'unread') {
                nextUnreadCount = Math.max(0, nextUnreadCount - 1);
            }
        }

        if (packet.operation === 'read-all') {
            items = [];
            nextUnreadCount = 0;
        }

        set({
            hydrated: true,
            items,
            latestToastItem:
                packet.operation === 'create'
                    ? (packet.payload as InboxItem)
                    : packet.operation === 'delete' && currentState.latestToastItem?.id === packet.entityId
                      ? undefined
                      : currentState.latestToastItem,
            deletedItemIds:
                packet.operation === 'delete'
                    ? [...currentState.deletedItemIds, packet.entityId]
                    : currentState.deletedItemIds,
            unreadCount: nextUnreadCount,
        });
    },
    fetchPreview: async (force = false) => {
        if (get().loading || (get().hydrated && !force)) {
            return;
        }

        const itemIdsAtRequestStart = new Set(get().items.map((item) => item.id));

        set({ error: undefined, loading: true });

        try {
            const page = await inboxService.list({ limit: PREVIEW_LIMIT, status: 'unread' });

            // A realtime create can arrive while the preview request is in flight. Preserve
            // those newly arrived items so the response cannot replace the optimistic state
            // with an older server snapshot.
            const realtimeItems = get().items.filter((item) => !itemIdsAtRequestStart.has(item.id));
            const deletedIds = new Set(get().deletedItemIds);
            const items = mergeInboxItems(
                page.items.filter((item) => !deletedIds.has(item.id)),
                realtimeItems,
            );
            const pageItemIds = new Set(page.items.map((item) => item.id));
            const realtimeUnreadCount = realtimeItems.filter(
                (item) => item.status === 'unread' && !pageItemIds.has(item.id),
            ).length;

            set({
                hydrated: true,
                items,
                loading: false,
                unreadCount: Math.max(
                    0,
                    page.total - page.items.filter((item) => deletedIds.has(item.id)).length + realtimeUnreadCount,
                ),
            });
        } catch (error) {
            captureException(error);

            set({ error: 'Failed to load notifications', loading: false });
        }
    },
    deleteItem: async (item) => {
        if (get().deletingItemIds.includes(item.id) || get().deletedItemIds.includes(item.id)) {
            return;
        }
        set({ deletingItemIds: [...get().deletingItemIds, item.id], error: undefined });
        try {
            const response = await inboxService.deleteItem(item.activity.id);
            const packet: InboxNotificationDeltaPacket = {
                v: 1,
                type: 'delta:packet',
                entity: 'inbox',
                operation: 'delete',
                entityId: item.id,
                payload: { activityId: item.activity.id, wasUnread: response.wasUnread },
                workspaceId: item.activity.workspace?.id,
                timestamp: Date.now(),
            };
            get().applyInboxDelta(packet);
            publishLocalDeltaPacket(packet);
        } catch (error) {
            captureException(error, { tags: { action: 'notification.delete' } });
            set({ error: 'Failed to delete notification' });
            throw error;
        } finally {
            set({ deletingItemIds: get().deletingItemIds.filter((id) => id !== item.id) });
        }
    },
    markAllRead: async () => {
        const previousItems = get().items;
        const previousUnreadCount = get().unreadCount;

        set({ items: [], unreadCount: 0 });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'inbox',
            operation: 'read-all',
            entityId: 'all',
            timestamp: Date.now(),
        });

        try {
            await inboxService.markRead();
        } catch (error) {
            captureException(error);

            set({
                error: 'Failed to mark notifications read',
                items: previousItems.filter((item) => !get().deletedItemIds.includes(item.id)),
                unreadCount: Math.max(
                    0,
                    previousUnreadCount -
                        previousItems.filter(
                            (item) => item.status === 'unread' && get().deletedItemIds.includes(item.id),
                        ).length,
                ),
            });

            throw error;
        }
    },
    markItemRead: async (activityId) => {
        const previousItems = get().items;
        const previousUnreadCount = get().unreadCount;
        const item = previousItems.find((candidate) => candidate.activity.id === activityId);
        const items = previousItems.filter((candidate) => candidate.activity.id !== activityId);

        set({ items, unreadCount: Math.max(0, get().unreadCount - 1) });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'inbox',
            operation: 'read',
            entityId: item?.id ?? `resource-activity:${activityId}`,
            payload: { activityId },
            timestamp: Date.now(),
        });

        try {
            await inboxService.markItemRead(activityId);
        } catch (error) {
            captureException(error);

            set({
                error: 'Failed to mark notification read',
                items: previousItems.filter((item) => !get().deletedItemIds.includes(item.id)),
                unreadCount: Math.max(
                    0,
                    previousUnreadCount -
                        previousItems.filter(
                            (item) => item.status === 'unread' && get().deletedItemIds.includes(item.id),
                        ).length,
                ),
            });

            throw error;
        }
    },
}));
